import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
    Application,
    DashboardClaim,
    ListNotificationSettingsRequest,
    UserNotificationEventType
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreateOrUpdateNotificationSettingRequest,
    ListNotificationSettingsService,
    UpdateNotificationSettingService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    LifecycleAware,
    Orientation,
    ClaimService,
    FeatureFlagService
} from '@lsq-frontend/common-ui';
import {
    GetIsSellerManageAllPaymentsService,
    GetIsSellerManageAllPaymentsRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    GetRevenueShareStatusService,
    GetRevenueShareStatusRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first } from 'rxjs/operators';
import { GroupedNotificationPreferencesService } from '../../services/grouped-notification-preferences.service';
import {
    INotificationPreferencesGroup,
    INotificationPreferencesUiControl,
    NotificationPreferencesState
} from '../settings.models';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

@LifecycleAware()
@Component({
    selector: 'notification-preferences',
    templateUrl: './notification-preferences.component.html',
    styleUrls: ['./notification-preferences.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NotificationPreferencesComponent implements OnDestroy, OnInit {
    public readonly titleI18n = this._i18n({
        value: 'Notification Preferences',
        description: 'Page title'
    });
    public readonly updatesTitleI18n = this._i18n({
        value: 'Updates',
        description: 'Section title'
    });
    public readonly updatesDescriptionI18n = this._i18n({
        value:
            'Stay up to date with activity on your account - select how and what type of notifications you receive.',
        description: 'Section description'
    });
    public readonly inAppTitleI18n = this._i18n({
        value: 'In-App',
        meaning: 'Notifications within the application',
        description: 'Section title'
    });

    public readonly newInvoicesAvailableTitle = this._i18n({
        value: 'New Invoices Available'
    });
    public readonly newInvoicesAvailableDescription = this._i18n({
        value:
            'Get an update when new invoices become available or are scheduled for early payment.'
    });
    public readonly fundingSentTitle = this._i18n({
        value: 'Early Payment Sent'
    });
    public readonly fundingSentDescription = this._i18n({
        value: 'Get updates when your early payment request has been sent.'
    });
    public readonly paymentMethodUpdatesTitle = this._i18n({
        value: 'Payment Method Updates'
    });
    public readonly paymentMethodUpdatesDescription = this._i18n({
        value: 'Get notified anytime there is a change or addition of a payment method.'
    });
    public readonly paymentOnTermsSentTitle = this._i18n({
        value: 'Payment Sent'
    });
    public readonly paymentOnTermsSentDescription = this._i18n({
        value: 'Get updates when a payment has been sent for invoices that were not early paid.'
    });
    public readonly newUserTitle = this._i18n({
        value: 'New User Added'
    });
    public readonly newUserDescription = this._i18n({
        value:
            'Be notified when a user has accepted their invite and is able to access LSQ FastTrack.'
    });

    public readonly paymentsPastDueTitle = this._i18n({
        value: 'Payments Past Due'
    });
    public readonly paymentsPastDueDescription = this._i18n({
        value: 'Receive an email when new early paid invoices become due.'
    });
    public readonly newVendorEnrolledTitle = this._i18n({
        value: 'New Supplier Enrolled'
    });
    public readonly newVendorEnrolledDescription = this._i18n({
        value: 'Get a notification when a new supplier has enrolled for FastTrack.'
    });
    public readonly dailyEarlyPaymentTitle = this._i18n({
        value: 'Early Payment'
    });
    public readonly dailyEarlyPaymentDescription = this._i18n({
        value: 'Get a daily notification on invoices that were submitted for early pay.'
    });
    public readonly paymentConfirmationTitle = this._i18n({
        value: 'Payment Confirmation'
    });
    public readonly paymentConfirmationDescription = this._i18n({
        value: 'Get a notification when your payment has been received and processed.'
    });
    public readonly weeklyActivityReportTitle = this._i18n({
        value: 'Weekly Activity Report'
    });
    public readonly weeklyActivityReportDescription = this._i18n({
        value: "Receive your supplier's weekly activity and your new balance every Friday."
    });
    public readonly monthlyStatementTitle = this._i18n({
        value: 'Monthly Fee Statement'
    });
    public readonly monthlyStatementDescription = this._i18n({
        value: 'Get a statement every month of your fees for LSQ FastTrack.'
    });
    public readonly quarterlyRevenueReportTitle = this._i18n({
        value: 'Quarterly Revenue Report'
    });
    public readonly quarterlyRevenueReportDescription = this._i18n({
        value: 'Get a report every quarter on your total rebates from using LSQ FastTrack.'
    });
    public readonly paymentReminderTitle = this._i18n({
        value: 'Payment Reminder'
    });
    public readonly paymentReminderDescription = this._i18n({
        value: 'Get a notification on upcoming payments due within 5 calendar days.'
    });
    public readonly fileConfirmationReportTitle = this._i18n({
        value: 'File Confirmation Report'
    });
    public readonly fileConfirmationReportDescription = this._i18n({
        value: 'Get a confirmation report for your uploaded files.'
    });
    public readonly paymentReconciliationTitle = this._i18n({
        value: 'Payment Reconciliation'
    });
    public readonly paymentReconciliationDescription = this._i18n({
        value: 'Get a notification of payment reconciliation.'
    });

    public isFactoringUser!: boolean;
    public isBuyerUser!: boolean;
    public isDirectSupplierUser!: boolean;
    public isPlatformSupplierUser!: boolean;

    public orientation = Orientation;

    private _supplierSettingsKeys: { [key: string]: UserNotificationEventType } = {
        newInvoicesAvailable: UserNotificationEventType.NewInvoicesAvailable,
        earlyPaymentFundingSent: UserNotificationEventType.EarlyPaymentFundingSent,
        paymentAtTermsSent: UserNotificationEventType.PaymentAtTermsSent,
        paymentMethodUpdates: UserNotificationEventType.BankAccountUpdated,
        newUserAdded: UserNotificationEventType.NewUserAdded
    };

    private _directSupplierSettings = new Map<
        UserNotificationEventType,
        INotificationPreferencesUiControl
    >([
        [
            this._supplierSettingsKeys.newInvoicesAvailable,
            {
                title: this.newInvoicesAvailableTitle,
                label: this.newInvoicesAvailableDescription,
                id: 'NewInvoicesControl',
                notificationType: UserNotificationEventType.NewInvoicesAvailable,
                emailIsOn: true
            }
        ],
        [
            this._supplierSettingsKeys.earlyPaymentFundingSent,
            {
                title: this.fundingSentTitle,
                label: this.fundingSentDescription,
                id: 'EarlyPaymentsSentControl',
                notificationType: UserNotificationEventType.EarlyPaymentFundingSent,
                emailIsOn: true
            }
        ],
        [
            this._supplierSettingsKeys.paymentMethodUpdates,
            {
                title: this.paymentMethodUpdatesTitle,
                label: this.paymentMethodUpdatesDescription,
                id: 'PaymentMethodUpdatesControl',
                notificationType: UserNotificationEventType.BankAccountUpdated,
                emailIsOn: true
            }
        ],
        [
            this._supplierSettingsKeys.paymentAtTermsSent,
            {
                title: this.paymentOnTermsSentTitle,
                label: this.paymentOnTermsSentDescription,
                id: 'PaymentAtTermsSentControl',
                notificationType: UserNotificationEventType.PaymentAtTermsSent,
                emailIsOn: true
            }
        ],
        [
            this._supplierSettingsKeys.newUserAdded,
            {
                title: this.newUserTitle,
                label: this.newUserDescription,
                id: 'NewUserAddedControl',
                notificationType: UserNotificationEventType.NewUserAdded,
                emailIsOn: true
            }
        ]
    ]);

    private _platformSupplierSettings = new Map<
        UserNotificationEventType,
        INotificationPreferencesUiControl
    >([
        [
            this._supplierSettingsKeys.paymentMethodUpdates,
            {
                title: this.paymentMethodUpdatesTitle,
                label: this.paymentMethodUpdatesDescription,
                id: 'PaymentMethodUpdatesControl',
                notificationType: UserNotificationEventType.BankAccountUpdated,
                emailIsOn: true
            }
        ],
        [
            this._supplierSettingsKeys.earlyPaymentFundingSent,
            {
                title: this.fundingSentTitle,
                label: this.fundingSentDescription,
                id: 'EarlyPaymentsSentControl',
                notificationType: UserNotificationEventType.EarlyPaymentFundingSent,
                emailIsOn: true
            }
        ]
    ]);

    private _buyerSettingsKeys = {
        newVendorEnrolled: UserNotificationEventType.NewVendorEnrolled,
        dailyEarlyPaymentReport: UserNotificationEventType.DailyEarlyPaymentReport,
        paymentConfirmation: UserNotificationEventType.PaymentConfirmation,
        paymentReminder: UserNotificationEventType.PaymentReminder,
        paymentsPastDue: UserNotificationEventType.PaymentsPastDue,
        weeklyActivityReport: UserNotificationEventType.WeeklyActivityReport,
        quarterlyRevenueReport: UserNotificationEventType.QuarterlyRevenueReport,
        newUserAdded: UserNotificationEventType.NewUserAdded,
        monthlyStatement: UserNotificationEventType.MonthlyStatement,
        fileConfirmationReport: UserNotificationEventType.FileConfirmationReport,
        paymentReconciliation: UserNotificationEventType.PaymentReconciliation
    };

    private _buyerSettings = new Map<UserNotificationEventType, INotificationPreferencesUiControl>([
        [
            this._buyerSettingsKeys.newVendorEnrolled,
            {
                title: this.newVendorEnrolledTitle,
                label: this.newVendorEnrolledDescription,
                id: 'NewVendorEnrolledControl',
                notificationType: UserNotificationEventType.NewVendorEnrolled,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.dailyEarlyPaymentReport,
            {
                title: this.dailyEarlyPaymentTitle,
                label: this.dailyEarlyPaymentDescription,
                id: 'DailyEarlyPaymentReportControl',
                notificationType: UserNotificationEventType.DailyEarlyPaymentReport,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.paymentConfirmation,
            {
                title: this.paymentConfirmationTitle,
                label: this.paymentConfirmationDescription,
                id: 'PaymentConfirmationControl',
                notificationType: UserNotificationEventType.PaymentConfirmation,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.paymentReminder,
            {
                title: this.paymentReminderTitle,
                label: this.paymentReminderDescription,
                id: 'PaymentReminderControl',
                notificationType: UserNotificationEventType.PaymentReminder,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.paymentsPastDue,
            {
                title: this.paymentsPastDueTitle,
                label: this.paymentsPastDueDescription,
                id: 'PaymentsPastDueControl',
                notificationType: UserNotificationEventType.PaymentsPastDue,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.weeklyActivityReport,
            {
                title: this.weeklyActivityReportTitle,
                label: this.weeklyActivityReportDescription,
                id: 'WeeklyActivityReportControl',
                notificationType: UserNotificationEventType.WeeklyActivityReport,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.quarterlyRevenueReport,
            {
                title: this.quarterlyRevenueReportTitle,
                label: this.quarterlyRevenueReportDescription,
                id: 'QuarterlyRevenueReportControl',
                notificationType: UserNotificationEventType.QuarterlyRevenueReport,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.newUserAdded,
            {
                title: this.newUserTitle,
                label: this.newUserDescription,
                id: 'NewUserAddedControl',
                notificationType: UserNotificationEventType.NewUserAdded,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.monthlyStatement,
            {
                title: this.monthlyStatementTitle,
                label: this.monthlyStatementDescription,
                id: 'MonthlyStatementControl',
                notificationType: UserNotificationEventType.MonthlyStatement,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.fileConfirmationReport,
            {
                title: this.fileConfirmationReportTitle,
                label: this.fileConfirmationReportDescription,
                id: 'FileConfirmationReportControl',
                notificationType: UserNotificationEventType.FileConfirmationReport,
                emailIsOn: true
            }
        ],
        [
            this._buyerSettingsKeys.paymentReconciliation,
            {
                title: this.paymentReconciliationTitle,
                label: this.paymentReconciliationDescription,
                id: 'PaymentReconciliationControl',
                notificationType: UserNotificationEventType.PaymentReconciliation,
                emailIsOn: true
            }
        ]
    ]);

    public notificationSettings: INotificationPreferencesUiControl[] = [];

    constructor(
        private _i18n: I18n,
        private _groupedNotificationPreferencesService: GroupedNotificationPreferencesService,
        private _claimService: ClaimService,
        private _userService: UserService,
        private _listNotificationSettingsService: ListNotificationSettingsService,
        private _updateNotificationSettingService: UpdateNotificationSettingService,
        private _getRevenueShareStatusService: GetRevenueShareStatusService,
        private _featureFlagService: FeatureFlagService,
        private _getIsSellerManageAllPayments: GetIsSellerManageAllPaymentsService
    ) {
        this.isFactoringUser = this._claimService.isOnSync(DashboardClaim.FactoringDashboard);
        this.isBuyerUser = this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal);
        this.isDirectSupplierUser = this._claimService.isOnSync(
            DashboardClaim.FinancingVendorPortal
        );
        this.isPlatformSupplierUser = this._claimService.isOnSync(
            DashboardClaim.FinancingPlatformEnrollment
        );
    }

    public async ngOnInit(): Promise<void> {
        const user = await this._userService.user$.pipe(first()).toPromise();
        if (!this.isFactoringUser) {
            const response = await this._listNotificationSettingsService.executeOnce(
                new ListNotificationSettingsRequest({
                    companyKey: user?.currentCompany?.companyKey,
                    application: Application.FinancingVendorPortal
                })
            );

            if (this.isDirectSupplierUser) {
                const { isMap } = await this._getIsSellerManageAllPayments.executeOnce(
                    new GetIsSellerManageAllPaymentsRequest({
                        sellerKey: user.currentCompany.alternateCompanyKey
                    })
                );
                if (!isMap) {
                    this._directSupplierSettings.delete(
                        UserNotificationEventType.PaymentAtTermsSent
                    );
                }
                response.rows.forEach((notificationSetting) => {
                    const foundSetting = this._directSupplierSettings.get(
                        notificationSetting.userNotificationEventType
                    );
                    if (foundSetting) {
                        this._directSupplierSettings.set(
                            notificationSetting.userNotificationEventType,
                            {
                                ...foundSetting,
                                emailIsOn: notificationSetting.sendEmail
                            }
                        );
                    }
                });

                this.notificationSettings = Array.from(this._directSupplierSettings.values());
            } else if (this.isPlatformSupplierUser) {
                response.rows.forEach((notificationSetting) => {
                    const foundSetting = this._platformSupplierSettings.get(
                        notificationSetting.userNotificationEventType
                    );
                    if (foundSetting) {
                        this._platformSupplierSettings.set(
                            notificationSetting.userNotificationEventType,
                            {
                                ...foundSetting,
                                emailIsOn: notificationSetting.sendEmail
                            }
                        );
                    }
                });

                this.notificationSettings = Array.from(this._platformSupplierSettings.values());
            } else if (this.isBuyerUser) {
                if (!(await this._isRevenueShareEnabled())) {
                    this._buyerSettings.delete(this._buyerSettingsKeys.quarterlyRevenueReport);
                }
                response.rows.forEach((notificationSetting) => {
                    const foundSetting = this._buyerSettings.get(
                        notificationSetting.userNotificationEventType
                    );
                    if (foundSetting) {
                        this._buyerSettings.set(notificationSetting.userNotificationEventType, {
                            ...foundSetting,
                            emailIsOn: notificationSetting.sendEmail
                        });
                    }
                });

                this.notificationSettings = Array.from(this._buyerSettings.values());
            }
        } else {
            await this._groupedNotificationPreferencesService.populateNotificationPreferences();
        }
    }

    public ngOnDestroy(): void {}

    public async handlePreferenceChange(
        type: UserNotificationEventType,
        value: boolean
    ): Promise<void> {
        if (this.isFactoringUser) {
            await this._groupedNotificationPreferencesService.updatePreferences(type, value);
        } else {
            const user = await this._userService.user$.pipe(first()).toPromise();
            await this._updateNotificationSettingService.executeOnce(
                new CreateOrUpdateNotificationSettingRequest({
                    userKey: user.isImpersonating ? user.impersonationKey : user.userKey,
                    companyKey: user.currentCompany.companyKey,
                    userNotificationEventType: type,
                    sendEmail: value
                })
            );
        }
    }

    public get notificationPreferencesGroups(): INotificationPreferencesGroup[] {
        return this._notificationPreferencesState
            ? this._notificationPreferencesState.notificationPreferencesGroups
            : [];
    }

    private get _notificationPreferencesState(): NotificationPreferencesState {
        return this._groupedNotificationPreferencesService.notificationPreferencesState;
    }

    private async _isRevenueShareEnabled(): Promise<boolean> {
        let revenueShareIsEnabled = false;
        try {
            const { revenueShareStatus } = await this._getRevenueShareStatusService.executeOnce(
                new GetRevenueShareStatusRequest()
            );
            revenueShareIsEnabled = revenueShareStatus.isEnabled;
        } catch {
            // Do nothing.
        }
        return revenueShareIsEnabled;
    }
}
